import React, { useState, useEffect } from 'react';
import { View, LogBox, ScrollView } from 'react-native';
import { interpolateGnBu, interpolateBlues } from 'd3-scale-chromatic';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { Loader } from '../../Common/Loader';
import { colorType3, colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import LogCountCard from './LogCountCard';
import { useNav } from '../../../providers/NavProvider';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function RegionalMaterial(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const [LogData, setLogData] = useState<null | any>(null);
  const [LogColumns, setLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  useEffect(() => {
    async function fetchData() {
      try {
        const logTitle = props.logTitle;
        const accessToken = await getAccessToken();
        const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId);
        setLogData(json.items);
        setLogColumns(json.columns);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, []);

  return LogData && LogColumns
    ? <CreateNoiseLogCharts LogData={LogData} LogColumns={LogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateNoiseLogChartsProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateNoiseLogCharts: React.FC<CreateNoiseLogChartsProps> = ({ LogData, LogColumns, siteId, logTitle, clientAPIURL }) => {

  const { getAccessToken } = useAuth();
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const [filteredData, setFilteredData] = useState<any[]>(LogData ?? []);

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const newItem = { ...result.items[0] };
        setFilteredData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  // Pie 1: entry count by CSI type
  const pieAccum1: Record<string, number> = {};
  filteredData.forEach((item: any) => {
    if (item.CSI == null) return;
    const k = String(item.CSI);
    pieAccum1[k] = (pieAccum1[k] ?? 0) + 1;
  });
  const byCSITypePieData = Object.entries(pieAccum1).map(([name, value], index) => ({
    name,
    value,
    color: colorType5(index.toString()),
  }));

  // Pie 2: MaterialValue summed by CSI type
  const pieAccum2: Record<string, number> = {};
  filteredData.forEach((item: any) => {
    if (item.CSI == null) return;
    const k = String(item.CSI);
    pieAccum2[k] = (pieAccum2[k] ?? 0) + (Number(item.MaterialValue) || 0);
  });
  const byCSIMaterialValuePieData = Object.entries(pieAccum2).map(([name, value], index) => ({
    name,
    value: Math.round(value),
    color: colorType3(index.toString()),
  }));

  const TotalCount = filteredData.reduce((total, item) => {
    return total + (Number(item.MaterialValue) || 0);
  }, 0);

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab && defaultTab === 'list' ? 1 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={{ zIndex: 200, alignSelf: 'flex-end' }}>
                <LogDateRangeFilter data={LogData} onFilterChange={setFilteredData} />
              </View>
              <View style={[logStyles.container]} key={0}>
                <View style={logStyles.card}>
                  <LogCountCard cardTitle={'Total Regional Material Value'} number={Math.round(TotalCount)} />
                </View>
              </View>
              <View>
                <View style={[logStyles.container]}>
                  <View key={1} style={[logStyles.card, { backgroundColor: '' }]}>
                    {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                    <PieChartComp data={byCSITypePieData} colorPallate={interpolateBlues} subTitle={'(%)'} ChartTitle={"CSI Type"} />
                  </View>
                  <View key={2} style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={byCSIMaterialValuePieData} colorPallate={interpolateGnBu} subTitle={'(Regional Material (%) / CSI)'} ChartTitle={"Material Value"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={() => {}}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
